import { useCallback, useEffect, useState } from 'react';
import useAlert from '../common/hooks/useAlert';
import { AlertVariant } from '../common/providers/alert.provider';
import { USER_REJECTED } from '../common/web3/mp-wallet';
import {
  connectWallet as connectWalletSession,
  disconnectWallet as disconnectWalletSession,
  getWalletSnapshot,
  restoreWallet,
  subscribeWallet,
  walletErrorMessage,
  WalletSnapshot,
} from '../common/web3/wallet-session';

export type { WalletStatus } from '../common/web3/wallet-session';

type DisconnectWalletOptions = {
  silent?: boolean;
};

export const useMetaMask = () => {
  const { showAlert } = useAlert();
  const [wallet, setWallet] = useState<WalletSnapshot>(getWalletSnapshot);

  useEffect(() => {
    const unsubscribe = subscribeWallet(setWallet);
    setWallet(getWalletSnapshot());
    restoreWallet();
    return unsubscribe;
  }, []);

  const connectWallet = useCallback(
    async (chainId?: unknown): Promise<void> => {
      try {
        const address = await connectWalletSession(
          typeof chainId === 'number' ? { chainId } : {},
        );
        if (address) {
          showAlert('MetaMask wallet connected.', AlertVariant.SUCCESS);
        } else if (getWalletSnapshot().status === 'unverified') {
          showAlert(
            'Wallet connected. Tap Verify Wallet to confirm it is yours in MetaMask.',
            AlertVariant.INFO,
          );
        }
      } catch (error: any) {
        if (error?.code === USER_REJECTED) {
          showAlert('The MetaMask request was cancelled.', AlertVariant.INFO);
        } else {
          showAlert(walletErrorMessage(error), AlertVariant.ERROR);
        }
      }
    },
    [showAlert],
  );

  const disconnectWallet = useCallback(
    async (options: DisconnectWalletOptions = {}): Promise<void> => {
      try {
        await disconnectWalletSession();
        if (!options.silent) {
          showAlert('MetaMask wallet disconnected.', AlertVariant.SUCCESS);
        }
      } catch (error: any) {
        if (!options.silent) {
          showAlert(
            `Could not disconnect wallet. ${walletErrorMessage(error)}`,
            AlertVariant.ERROR,
          );
        }
      }
    },
    [showAlert],
  );

  const { account, status } = wallet;

  return {
    account,
    connectWallet,
    disconnectWallet,
    isConnecting: status === 'connecting' || status === 'verifying',
    isDisconnecting: status === 'disconnecting',
    isRestoring: status === 'restoring',
    walletStatus: status,
  };
};

export default useMetaMask;
